import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { ItemErrorCodes } from '@nohara-dxdev/mt-common';
import { useNotifications } from '@nohara-dxdev/react-core';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/confirm-dialog';

import { useDeleteItem } from '../api';

interface DeleteItemProps {
  itemId: string;
}

export const DeleteItem = ({ itemId }: DeleteItemProps) => {
  const [open, setOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const { t } = useTranslation();
  const notifications = useNotifications();
  const deleteItem = useDeleteItem({
    mutationConfig: {
      onSuccess: () => {
        notifications.success('items.delete.success');
      },
      onError: () => {
        notifications.error('items.delete.error');
      },
    },
  });

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
  };

  const handleConfirm = async () => {
    try {
      await deleteItem.mutateAsync(itemId);
      handleClose();
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.status === 400 &&
        error.response.data?.message === ItemErrorCodes.ITEM_HAS_INFRASTRUCTURE
      ) {
        handleClose();
        setErrorDialogOpen(true);
        return;
      }
      throw error;
    }
  };

  return (
    <>
      <Tooltip title={t('items.delete.tooltip', 'Delete item')}>
        <IconButton
          color="error"
          size="small"
          onClick={handleClick}
          disabled={deleteItem.isPending}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <ConfirmDialog
        open={open}
        title={t('items.delete.title', 'Delete Item')}
        message={t(
          'items.delete.message',
          'Are you sure you want to delete this item? This action cannot be undone.',
        )}
        onClose={handleClose}
        onConfirm={handleConfirm}
        confirmText={t('common.delete', 'Delete')}
        cancelText={t('common.cancel', 'Cancel')}
        loading={deleteItem.isPending}
      />

      <Dialog open={errorDialogOpen} onClose={handleErrorDialogClose}>
        <DialogTitle>{t('items.delete.infrastructureError.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText color="error">
            {t('items.delete.infrastructureError.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose} color="primary" autoFocus>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
